import { ColumnTypes, LamenessColumnKey } from 'models'
import { LicensesType } from 'models/IUserData'
import { useCallback, useMemo, useState } from 'react'
import { selectPermissions } from 'store/authSlice'
import { useAppSelector } from 'store/hooks'
import { showMilkYieldUi } from 'utils/companyMilkFeatures'
import { initialActiveColumn, titleForLamenessFilter } from '../processes'

const cloneColumns = (columns: ColumnTypes[]) =>
	columns.map(column => ({ ...column }))

const isSameColumnView = (left: ColumnTypes[], right: ColumnTypes[]) =>
	JSON.stringify(
		left.map(({ key, isShowColumn }) => ({ key, isShowColumn })),
	) ===
	JSON.stringify(right.map(({ key, isShowColumn }) => ({ key, isShowColumn })))

export const useActiveColumn = () => {
	const permissions = useAppSelector(selectPermissions)
	const { userData } = useAppSelector(state => state.userReducer)
	const canShowMilkYield = showMilkYieldUi(userData?.company?.id)
	const [columns, setColumns] = useState<ColumnTypes[]>(() =>
		cloneColumns(initialActiveColumn),
	)

	const filterAvailable = useCallback(
		(cols: ColumnTypes[]) => {
			return cols.filter(col => {
				if (
					!canShowMilkYield &&
					(col.key === LamenessColumnKey.LACTATION_DAY ||
						col.key === LamenessColumnKey.LACTATION_STATUS)
				) {
					return false
				}
				if (col.requiresLicense) {
					return permissions?.[col.requiresLicense] === true
				}
				return true
			})
		},
		[canShowMilkYield, permissions],
	)

	const availableColumns = useMemo(
		() => filterAvailable(columns),
		[columns, filterAvailable],
	)

	const defaultAvailableColumns = useMemo(
		() => filterAvailable(cloneColumns(initialActiveColumn)),
		[filterAvailable],
	)

	const isDefaultView = useMemo(
		() => isSameColumnView(availableColumns, defaultAvailableColumns),
		[availableColumns, defaultAvailableColumns],
	)

	const availableFilterOptions = useMemo(() => {
		return titleForLamenessFilter.filter(filter => {
			if (!canShowMilkYield && filter.key === 'lactation') {
				return false
			}
			if (filter.key === 'fatness') {
				return permissions?.[LicensesType.FATNESS] === true
			}
			return true
		})
	}, [canShowMilkYield, permissions])

	const getColumn = (key: LamenessColumnKey) => {
		return availableColumns.find(col => col.key === key)
	}

	const toggleColumn = (key: LamenessColumnKey) => {
		setColumns(prevColumns =>
			prevColumns.map(col =>
				col.key === key ? { ...col, isShowColumn: !col.isShowColumn } : col,
			),
		)
	}

	const toggleColumnByTitle = (title: string) => {
		setColumns(prevColumns =>
			prevColumns.map(col =>
				col.title === title ? { ...col, isShowColumn: !col.isShowColumn } : col,
			),
		)
	}

	const resetColumns = useCallback(() => {
		setColumns(cloneColumns(initialActiveColumn))
	}, [])

	return {
		columns: availableColumns,
		getColumn,
		toggleColumn,
		toggleColumnByTitle,
		resetColumns,
		isDefaultView,
		availableFilterOptions,
	}
}
